'use client';

import {
	EnvironmentConstants,
	RpcEndpoint,
	getResponseTimes,
} from '@velocity-exchange/common';
import { useEffect, useMemo, useRef, useState } from 'react';
import useCurrentSettings from './useCurrentSettings';
import useIsMainnet from './useIsMainnet';
import { isWindowDefined } from 'src/utils/browser';
import {
	RpcLatencies,
	useRpcLatencyStore,
} from 'src/stores/useRpcLatencyStore';

const average = (arr: number[]) => arr.reduce((p, c) => p + c, 0) / arr.length;

const REFRESH_RATE_SECONDS = 60;

const useRpcLatencies = (rpcOptions: RpcEndpoint[]) => {
	const isLocalHost =
		isWindowDefined() && window.location.href.includes('localhost');

	const initialState = rpcOptions.reduce<RpcLatencies>(
		(acc, rpc) => ({
			...acc,
			[rpc.value]: {
				avg: null,
				lastFiveLatencies: [],
			},
		}),
		{} as RpcLatencies
	);
	const [avgLatencies, setAvgLatencies] = useState<RpcLatencies>(initialState);

	const intervalIsRunning = useRef(false);

	useEffect(() => {
		const getAndSetLatencies = async () => {
			try {
				const responseTimes = await getResponseTimes(rpcOptions);

				if (responseTimes) {
					const newLatencies: Record<
						string,
						{ avg: number; lastFiveLatencies: number[] }
					> = {};
					responseTimes.forEach((rpc) => {
						const lastFiveLatencies = !rpc.latency
							? []
							: avgLatencies?.[rpc.value]?.lastFiveLatencies
								? [
										rpc.latency,
										...(avgLatencies?.[rpc.value]?.lastFiveLatencies ?? []),
									].slice(0, 5)
								: [rpc.latency];
						const avg = Math.ceil(average(lastFiveLatencies));
						newLatencies[rpc.value] = { avg, lastFiveLatencies };
					});
					setAvgLatencies(newLatencies);
				}
			} catch (err) {
				console.error(err);
			}
		};

		if (!intervalIsRunning.current) getAndSetLatencies();

		const interval = setInterval(
			getAndSetLatencies,
			(isLocalHost ? 10 : REFRESH_RATE_SECONDS) * 1000
		);

		intervalIsRunning.current = true;

		return () => clearInterval(interval);
	}, [avgLatencies, rpcOptions]);

	return avgLatencies;
};

/**
 * Mount ONCE (in `useAppSetup`). Polls every RPC endpoint's latency on an
 * interval and writes the result to `useRpcLatencyStore`, so all consumers share
 * a single poll. Replaces `singletonHook(useAllRpcLatencies_)`.
 */
export const useSyncAllRpcLatencies = () => {
	const isMainnet = useIsMainnet();
	const [settings] = useCurrentSettings();
	const setLatencies = useRpcLatencyStore((s) => s.setLatencies);

	const currentRpc = settings?.rpcEndpoint as RpcEndpoint | undefined;

	const rpcOptions = useMemo(() => {
		const opts = isMainnet
			? [...EnvironmentConstants.rpcs.mainnet]
			: [...EnvironmentConstants.rpcs.dev];

		if (
			currentRpc &&
			!opts.map((opt) => opt.value).includes(currentRpc.value)
		) {
			opts.push(currentRpc);
		}

		return opts;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isMainnet, currentRpc?.value]);

	const isLocalHost =
		isWindowDefined() && window.location.href.includes('localhost');

	useEffect(() => {
		let cancelled = false;

		const getAndSetLatencies = async () => {
			try {
				const responseTimes = await getResponseTimes(rpcOptions);
				if (!responseTimes || cancelled) return;

				const prev = useRpcLatencyStore.getState().latencies;
				const newLatencies: RpcLatencies = {};
				responseTimes.forEach((rpc) => {
					const lastFiveLatencies = !rpc.latency
						? []
						: prev?.[rpc.value]?.lastFiveLatencies
							? [rpc.latency, ...prev[rpc.value].lastFiveLatencies].slice(0, 5)
							: [rpc.latency];
					const avg = Math.ceil(average(lastFiveLatencies));
					newLatencies[rpc.value] = { avg, lastFiveLatencies };
				});
				setLatencies(newLatencies);
			} catch (err) {
				console.error(err);
			}
		};

		getAndSetLatencies();

		const interval = setInterval(
			getAndSetLatencies,
			(isLocalHost ? 10 : REFRESH_RATE_SECONDS) * 1000
		);

		return () => {
			cancelled = true;
			clearInterval(interval);
		};
	}, [rpcOptions, isLocalHost, setLatencies]);
};

export const useAllRpcLatencies = () => useRpcLatencyStore((s) => s.latencies);

export const useCurrentRpcLatency = () => {
	const [settings] = useCurrentSettings();

	const currentRpc = settings.rpcEndpoint;

	const currentRpcSpec = useMemo(() => {
		const rpcSpec: RpcEndpoint = {
			...currentRpc,
			allowAdditionalConnection: false,
		};

		return rpcSpec;
	}, [currentRpc?.value]);

	const latencies = useRpcLatencies([currentRpcSpec]);

	return currentRpcSpec
		? latencies[currentRpcSpec.value]
		: { avg: -1, lastFiveLatencies: [] };
};
